import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SystemAccessService } from '../services/system-access.service';
import { DemoModeService } from '../services/demo-mode.service';
import { MembershipService } from '../services/membership.service';
import { resolveStanding } from './standing.util';
import { SystemPermissionName } from '../models/system-access.model';

/**
 * Factory function that creates a guard checking for a specific system permission, or
 * any-of a set of permissions (OR semantic).
 *
 * Admin/Owner users pass through automatically (they have all permissions).
 * Regular members must hold at least one of the required permissions via their system role(s).
 *
 * @example
 * { path: 'analytics', canActivate: [permissionGuard('ViewAnalytics')] }
 * { path: 'groups',    canActivate: [permissionGuard(['ManageContentGroups', 'ManageSystemGroups'])] }
 */
export function permissionGuard(
  requiredPermission: SystemPermissionName | readonly SystemPermissionName[]
): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const demoModeService = inject(DemoModeService);
    const systemAccessService = inject(SystemAccessService);

    if (demoModeService.isDemo()) {
      // Every admission path records its basis — a demo bypass is never a parity admission
      // (a stale true from a previous route must not survive into this one).
      systemAccessService.setParityAdmission(false);
      return of(true);
    }

    const oidcSecurityService = inject(OidcSecurityService);
    const membershipService = inject(MembershipService);
    const router = inject(Router);
    const messageService = inject(MessageService);
    const translateService = inject(TranslateService);

    const required = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission as SystemPermissionName];

    return oidcSecurityService.isAuthenticated$.pipe(
      take(1),
      switchMap(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          // Store the attempted URL for redirecting after auth.
          sessionStorage.setItem('authRedirectUrl', state.url);
          // Redirect to portal — user can sign in from there
          return of(router.createUrlTree(['/']));
        }

        return from(Promise.all([
          systemAccessService.hasAnyPermission(required),
          systemAccessService.hasAnyRealPermission(required),
        ])).pipe(
          switchMap(async ([hasPermission, hasRealPermission]) => {
            // Platform-root admission: a root reaches every guarded surface; when the actor
            // holds NONE of the route's required permissions in their REAL (pre-union) list,
            // that basis is RECORDED — admin surfaces render their page-level read-only mode
            // off it. Computed from data, so it stays truthful even for routes guarded on keys
            // the monitoring read-grant set also contains. This is the single place admission
            // happens, so the signal always reflects the current route.
            const rootAdmit = systemAccessService.isRoot();
            if (hasPermission || rootAdmit) {
              systemAccessService.setParityAdmission(rootAdmit && !hasRealPermission);
              return true;
            }
            // A PAUSED member is walled with no toast and no warn — their redirect is a
            // corridor to the page that explains the state, not an access-denied event.
            const standing = await resolveStanding(membershipService);
            if (standing === 'paused' && !systemAccessService.isRoot()) {
              return router.createUrlTree(['/access-paused']);
            }
            // Non-member → portal; member without permission → home
            const target = systemAccessService.isMember() ? '/home' : '/';
            console.warn(`Access denied: one of [${required.join(', ')}] required for`, state.url);

            if (systemAccessService.loadError()) {
              messageService.add({
                severity: 'warn',
                summary: translateService.instant('common.error'),
                detail: translateService.instant('guards.permissionError')
              });
            } else {
              messageService.add({
                severity: 'warn',
                summary: translateService.instant('common.unauthorized'),
                detail: translateService.instant('guards.permissionDenied')
              });
            }

            return router.createUrlTree([target]);
          })
        );
      })
    );
  };
}
